import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import {
  Badge,
  Card,
  CardHeader,
  DataTable,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  Pagination,
  Select,
  Skeleton,
  Tabs,
  type Column,
} from '../../components/ui';
import { api, compact } from '../../lib/api';
import { TYPING_DEBOUNCE_MS, useDebounced } from '../../lib/debounce';
import { formatDateTime, formatNumber, humanize } from '../../lib/format';
import { useT } from '../../lib/i18n';
import type { Campaign, NotificationJob, Paginated } from '../../lib/types';

type HistoryView = 'campaigns' | 'jobs';

/**
 * What was sent, and what became of it.
 *
 * Two views because they answer different questions. **Sends** is "how did
 * that broadcast go"; **Scheduled** is "why did *this user* not get the
 * message", which is the one support actually asks — and the answer is almost
 * always one of the queue's skip reasons.
 */
export function HistoryTab() {
  const t = useT();
  const [view, setView] = useState<HistoryView>('campaigns');

  return (
    <>
      <Tabs
        label={t('notif.history.tabsLabel')}
        value={view}
        onChange={setView}
        tabs={[
          { id: 'campaigns', label: t('notif.history.tab.campaigns') },
          { id: 'jobs', label: t('notif.history.tab.jobs') },
        ]}
      />
      {view === 'campaigns' ? <Campaigns /> : <Jobs />}
    </>
  );
}

function Campaigns() {
  const t = useT();
  const [page, setPage] = useState(1);

  const campaigns = useQuery({
    queryKey: ['campaigns', page],
    queryFn: () =>
      api.get<Paginated<Campaign>>('/api/notifications/campaigns', {
        page,
        limit: 25,
      }),
    // A send in flight updates its counters as it goes.
    refetchInterval: 10_000,
  });

  const columns: Column<Campaign>[] = [
    {
      key: 'copy',
      header: t('notif.history.column.message'),
      render: (campaign) => (
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">
              {campaign.title ?? t('notif.history.fromTemplate')}
            </span>
            <StatusBadge status={campaign.status} />
          </div>
          {campaign.body && (
            <p className="mt-0.5 text-xs text-ink-2">{campaign.body}</p>
          )}
        </div>
      ),
    },
    {
      key: 'progress',
      header: t('notif.history.column.delivered'),
      numeric: true,
      render: (campaign) => (
        <span>
          {formatNumber(campaign.sentCount)} /{' '}
          {formatNumber(campaign.totalRecipients)}
        </span>
      ),
    },
    {
      key: 'skipped',
      header: t('notif.history.column.skipped'),
      numeric: true,
      desktopOnly: true,
      render: (campaign) => formatNumber(campaign.skippedCount),
    },
    {
      key: 'failed',
      header: t('notif.history.column.failed'),
      numeric: true,
      desktopOnly: true,
      render: (campaign) => formatNumber(campaign.failedCount),
    },
    {
      key: 'actor',
      header: t('notif.history.column.sentBy'),
      desktopOnly: true,
      render: (campaign) => (
        <span className="text-ink-2">{campaign.actor}</span>
      ),
    },
    {
      key: 'when',
      header: t('notif.history.column.when'),
      numeric: true,
      render: (campaign) => (
        <span className="text-ink-2">{formatDateTime(campaign.createdAt)}</span>
      ),
    },
  ];

  if (campaigns.isError) return <ErrorNote error={campaigns.error} />;

  return (
    <Card>
      <CardHeader
        title={t('notif.history.campaignsTitle')}
        description={t('notif.history.campaignsDescription')}
      />
      {campaigns.isPending ? (
        <Skeleton className="h-80" />
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={campaigns.data?.items ?? []}
            rowKey={(campaign) => campaign.id}
            empty={
              <EmptyState
                icon={<Send size={28} />}
                title={t('notif.history.emptyTitle')}
                description={t('notif.history.emptyDescription')}
              />
            }
          />
          <Pagination
            page={campaigns.data?.page ?? 1}
            totalPages={campaigns.data?.totalPages ?? 1}
            total={campaigns.data?.total ?? 0}
            onChange={setPage}
          />
        </>
      )}
    </Card>
  );
}

function Jobs() {
  const t = useT();
  const [status, setStatus] = useState('');
  const [deviceId, setDeviceId] = useState('');
  const [page, setPage] = useState(1);
  // Typed a character at a time; one request per pause, not per keystroke.
  const deviceQuery = useDebounced(deviceId.trim(), TYPING_DEBOUNCE_MS);

  const jobs = useQuery({
    queryKey: ['jobs', status, deviceQuery, page],
    queryFn: () =>
      api.get<Paginated<NotificationJob>>('/api/notifications/jobs', {
        ...compact({ status, deviceId: deviceQuery }),
        page,
        limit: 50,
      }),
  });

  const columns: Column<NotificationJob>[] = [
    {
      key: 'status',
      header: t('notif.history.column.outcome'),
      render: (job) => (
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={job.status} />
          {job.attempts > 1 && (
            <span className="text-xs text-ink-3">
              {t('notif.history.attempts', { count: job.attempts })}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'user',
      header: t('notif.history.column.device'),
      render: (job) => (
        <span className="font-mono text-xs break-anywhere">{job.deviceId}</span>
      ),
    },
    {
      key: 'result',
      header: t('notif.history.column.why'),
      render: (job) => (
        <span className="text-xs text-ink-2">{job.result ?? '—'}</span>
      ),
    },
    {
      key: 'runAt',
      header: t('notif.history.column.due'),
      numeric: true,
      render: (job) => (
        <span className="text-ink-2">{formatDateTime(job.runAt)}</span>
      ),
    },
    {
      key: 'settled',
      header: t('notif.history.column.settled'),
      numeric: true,
      desktopOnly: true,
      render: (job) => (
        <span className="text-ink-2">{formatDateTime(job.settledAt)}</span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid gap-3 rounded-xl border border-line bg-surface p-3.5 sm:grid-cols-2 sm:p-4">
        <Field label={t('notif.history.outcome')}>
          <Select
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setPage(1);
            }}
          >
            <option value="">{t('common.any')}</option>
            <option value="pending">{t('notif.history.status.pending')}</option>
            <option value="sent">{t('notif.history.status.sent')}</option>
            <option value="skipped">{t('notif.history.status.skipped')}</option>
            <option value="failed">{t('notif.history.status.failed')}</option>
            <option value="cancelled">
              {t('notif.history.status.cancelled')}
            </option>
          </Select>
        </Field>
        <Field label={t('common.deviceId')}>
          <Input
            className="font-mono text-xs"
            value={deviceId}
            onChange={(event) => {
              setDeviceId(event.target.value);
              setPage(1);
            }}
            placeholder={t('common.pasteDeviceId')}
          />
        </Field>
      </div>

      {jobs.isError && <ErrorNote error={jobs.error} />}

      <Card>
        <CardHeader
          title={t('notif.history.jobsTitle')}
          description={t('notif.history.jobsDescription')}
        />
        {jobs.isPending ? (
          <Skeleton className="h-80" />
        ) : (
          <>
            <DataTable
              columns={columns}
              rows={jobs.data?.items ?? []}
              rowKey={(job) => job.id}
              empty={
                <EmptyState
                  title={t('notif.history.jobsEmptyTitle')}
                  description={t('notif.history.jobsEmptyDescription')}
                />
              }
            />
            <Pagination
              page={jobs.data?.page ?? 1}
              totalPages={jobs.data?.totalPages ?? 1}
              total={jobs.data?.total ?? 0}
              onChange={setPage}
            />
          </>
        )}
      </Card>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const tones: Record<string, 'neutral' | 'brand' | 'good' | 'warn' | 'danger'> =
    {
      pending: 'neutral',
      processing: 'brand',
      sending: 'brand',
      sent: 'good',
      completed: 'good',
      skipped: 'warn',
      failed: 'danger',
      cancelled: 'neutral',
    };
  return <Badge tone={tones[status] ?? 'neutral'}>{humanize(status)}</Badge>;
}
